import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const USERS_COLLECTION = 'users';

/**
 * Save leads calculator data for the current user
 * @param {Object} formData - The form data to save
 * @param {Object} results - The calculation results
 * @returns {Promise<Object>} Saved data with metadata
 */
export const saveLeadsCalculatorData = async (formData, results) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to save leads calculator data');
    }

    const userDocRef = doc(db, USERS_COLLECTION, auth.currentUser.uid);
    
    // Get current user data to preserve existing fields
    const userDoc = await getDoc(userDocRef);
    const currentUserData = userDoc.exists() ? userDoc.data() : {};

    // Prepare the leads calculator data with metadata
    const leadsCalculatorDataWithMetadata = {
      formData: {
        revenue: formData.revenue || 0,
        averageJobSize: formData.averageJobSize || 0,
        closeRatio: formData.closeRatio || 0,
        qualifiedLeadPercent: formData.qualifiedLeadPercent || 0,
        // Set by the portal Lead Calculator tab; preserved when other callers
        // (e.g. the Analytics revenue-goal editor) save without it.
        costPerQualifiedLead:
          formData.costPerQualifiedLead ??
          currentUserData.leadsCalculator?.formData?.costPerQualifiedLead ??
          0
      },
      results: {
        contractsNeeded: results.contractsNeeded || 0,
        proposalsNeeded: results.proposalsNeeded || 0,
        leadsNeededAnnually: results.leadsNeededAnnually || 0,
        leadsNeededQuarterly: results.leadsNeededQuarterly || 0,
        leadsNeededMonthly: results.leadsNeededMonthly || 0,
        leadsNeededWeekly: results.leadsNeededWeekly || 0
      },
      lastUpdated: serverTimestamp(),
      version: (currentUserData.leadsCalculator?.version || 0) + 1
    };

    // Update user document with leads calculator data
    await updateDoc(userDocRef, {
      leadsCalculator: leadsCalculatorDataWithMetadata,
      lastUpdated: serverTimestamp()
    });

    console.log('✅ Leads calculator data saved successfully');
    return leadsCalculatorDataWithMetadata;
  } catch (error) {
    console.error('Error saving leads calculator data:', error);
    throw error;
  }
};

/**
 * Load leads calculator data for the current user
 * @returns {Promise<Object|null>} User's leads calculator data or null if not found
 */
export const loadLeadsCalculatorData = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to load leads calculator data');
    }

    const userDocRef = doc(db, USERS_COLLECTION, auth.currentUser.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      console.log('User document does not exist');
      return null;
    }

    const userData = userDoc.data();
    return userData.leadsCalculator || null;
  } catch (error) {
    console.error('Error loading leads calculator data:', error);
    throw error;
  }
};

/**
 * Load leads calculator data for a specific user (for portal accounts / CRM viewing)
 * @param {string} userId - The user ID to load data for
 * @returns {Promise<Object|null>} The saved leads calculator data, or null
 */
export const loadLeadsCalculatorDataForUser = async (userId) => {
  try {
    if (!userId) return null;
    const userDocRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userDocRef);
    if (!userDoc.exists()) return null;
    return userDoc.data().leadsCalculator || null;
  } catch (error) {
    console.error('Error loading leads calculator data for user:', error);
    throw error;
  }
};

/**
 * Save leads calculator data for a specific user (for portal accounts)
 * @param {string} userId - The user ID to save data for
 * @param {Object} formData - The form data to save
 * @param {Object} results - The calculation results
 * @returns {Promise<Object>} Saved data with metadata
 */
export const saveLeadsCalculatorDataForUser = async (userId, formData, results) => {
  try {
    const userDocRef = doc(db, USERS_COLLECTION, userId);
    
    // Get current user data to preserve existing fields
    const userDoc = await getDoc(userDocRef);
    const currentUserData = userDoc.exists() ? userDoc.data() : {};

    // Prepare the leads calculator data with metadata
    const leadsCalculatorDataWithMetadata = {
      formData: {
        revenue: formData.revenue || 0,
        averageJobSize: formData.averageJobSize || 0,
        closeRatio: formData.closeRatio || 0,
        qualifiedLeadPercent: formData.qualifiedLeadPercent || 0,
        // Set by the portal Lead Calculator tab; preserved when other callers
        // (e.g. the Analytics revenue-goal editor) save without it.
        costPerQualifiedLead:
          formData.costPerQualifiedLead ??
          currentUserData.leadsCalculator?.formData?.costPerQualifiedLead ??
          0
      },
      results: {
        contractsNeeded: results.contractsNeeded || 0,
        proposalsNeeded: results.proposalsNeeded || 0,
        leadsNeededAnnually: results.leadsNeededAnnually || 0,
        leadsNeededQuarterly: results.leadsNeededQuarterly || 0,
        leadsNeededMonthly: results.leadsNeededMonthly || 0,
        leadsNeededWeekly: results.leadsNeededWeekly || 0
      },
      lastUpdated: serverTimestamp(),
      version: (currentUserData.leadsCalculator?.version || 0) + 1
    };

    // Update user document with leads calculator data
    await updateDoc(userDocRef, {
      leadsCalculator: leadsCalculatorDataWithMetadata,
      lastUpdated: serverTimestamp()
    });

    console.log('✅ Leads calculator data saved successfully for user:', userId);
    return leadsCalculatorDataWithMetadata;
  } catch (error) {
    console.error('Error saving leads calculator data for user:', error);
    throw error;
  }
};
